import { defineComponent as _defineComponent } from 'vue'
import { apiGet, apiPut } from '@/store/api';
import { usePortcallStore } from '@/store/portcallStore';
import { useAppStatusStore } from '@/store/appStatusStore';
import { useApplicationStore } from '@/store/applicationStore';
import { GlobalHelperFunctions } from '@/common/GlobalHelperFunctions';
import WmButtonGroup from '@/app-components/WmButtonGroup.vue';
import EmailGenerator from '@/app-components/EmailGenerator.vue';
import { computed, ref, watch } from 'vue';
import { useHelperStore } from '@/store/helperStore';
import router from '@/router/router';

interface EmailItem {
  ID: string;
  TITLE: string;
  eventName: string;
  icon: string;
}
interface ProformaItem {
  proformaId: string;
  proformaNumber: number;
  sum: number;
  clientName: string;
  vat: number;
  total: number;
  discount: number;
  surcharge: number;
  comment: number;
}
interface GatResponseResult<T> {
  message: string;
  /** The response result */
  payload?: T;
  syncChanges: any;
  resultType: any;
}
interface ProformaCurrency {
  currencyName: string;
  proformaId: string;
  calculatedAmount: number;
}
interface ProformaListProps {
  callId: string;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'ProformaList',
  props: {
    callId: null
  },
  setup(__props: any) {

const props = __props as {
  callId: string;
};

// eslint-disable-next-line import/no-cycle

const appStatusStore = useAppStatusStore();
const applicationStore = useApplicationStore();
const portcallStore = usePortcallStore();
const helperStore = useHelperStore();
const isLoading = ref(false);
const proformaList = ref<ProformaItem[]>([]);
const selectedProforma = ref<number[]>([]);
const currenciesForProformas = ref<ProformaCurrency[]>([]);
const emailTemplateGuid = ref<string | undefined>(undefined);
const emailTemplates = ref<EmailItem[]>([]);
const agencyId = computed(() => portcallStore.callDetails.SETUP_ID);
const emailTemplateItems = computed(() => {
  const templates = [...emailTemplates.value];
  let result: any[] = [];
  const ignoreGroupMinCount = applicationStore.hasFeatureCode('IGNORE_DOCUMENT_GROUPING_MIN_COUNT');
  if (Array.isArray(templates) && templates.length > 0) {
    if (ignoreGroupMinCount ? true : templates.length > 10) {
      result = helperStore.emailTemplatesGroupedByLabels(templates, false);
    } else {
      result = templates.map((item) => ({
        caption: item.TITLE,
        eventName: 'createEmailClick',
        icon: 'mdi-file-outline',
        ID: item.ID,
      }));
    }
  }
  return result;
});
const userCanGenerateDoc = computed<boolean>(() => {
  const isInternal = applicationStore.user.internal;
  if (isInternal) {
    return true;
  }
  return true;
});
const selectedProformaId = computed<number>(() => {
  if (selectedProforma.value.length === 0) {
    return 0;
  }
  return selectedProforma.value[0];
});
const applicationStatus = computed(() => {
  const result = appStatusStore.getAppStatus;
  if (selectedProformaId.value) {
    result.ProformaId = selectedProformaId.value;
  }
  return result;
});
const wmButtonGroup = computed(() => {
  const result = [];
  result.push({
    btnType: 'CREATE_DOC',
    docType: 2,
    agencyId: agencyId.value,
    location: 'portcall',
    foreignKey: props.callId,
    applicationStatus: applicationStatus.value,
    disabled: !userCanGenerateDoc.value || selectedProformaId.value === 0,
  });
  result.push({
    btnType: 'CUSTOM',
    caption: 'Create email',
    icon: 'mdi-email',
    menuItems: emailTemplateItems.value,
    disabled: emailTemplateItems.value.length === 0 || selectedProformaId.value === 0,
  });
  return result;
});
const columns = computed(() => {
  const result: any[] = [
    { header: 'No', field: 'proformaNumber', dataType: 'string' },
    { header: 'Client name', field: 'clientName', dataType: 'string' },
    { header: 'Sum', field: 'sum', dataType: 'currency', decimals: 2 },
    {
      header: 'Discount',
      field: 'discount',
      dataType: 'currency',
      decimals: 2,
    },
    {
      header: 'Surcharge',
      field: 'surcharge',
      dataType: 'currency',
      decimals: 2,
    },
    { header: 'VAT', field: 'vat', dataType: 'currency', decimals: 2 },
    { header: 'Total', field: 'total', dataType: 'currency', decimals: 2 },
    { header: 'Comment', field: 'comment', dataType: 'string' },
  ];
  const uniqueCurrencies = Array.from(new Set(currenciesForProformas.value.map((i) => i.currencyName)));
  uniqueCurrencies.forEach((currencyName) => {
    result.push({
      header: currencyName,
      field: currencyName,
      dataType: 'string',
      format: '#,###.00',
      calculated: (rowdata: { proformaId: string }) => {
        const currencies = currenciesForProformas.value.filter(
          (currency) => rowdata.proformaId === currency.proformaId
        );
        if (!currencies || (currencies && currencies.length === 0)) {
          return '';
        }
        const currency = currencies.find((cur) => cur.currencyName === currencyName);
        if (!currency) {
          return '';
        }
        if (!currency.calculatedAmount) {
          return 0;
        }
        return currency.calculatedAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      },
    });
  });
  return result;
});
function emailGeneratorIsLoading(event: boolean) {
  if (!event) {
    emailTemplateGuid.value = undefined;
  }
}
async function getProformas(portcallId: string) {
  isLoading.value = true;
  try {
    const result = (await apiGet(`proforma/${portcallId}`)) as unknown as GatResponseResult<ProformaItem[]>;
    const currencies = (await apiGet(
      `proforma/GetCurrencySelectedForProformasInPortcall/${portcallId}`
    )) as unknown as GatResponseResult<ProformaCurrency[]>;
    if (result.payload) {
      proformaList.value = result.payload;
    }
    if (currencies.payload) {
      currenciesForProformas.value = currencies.payload;
    }
    const emailTemplatesResult = (await apiGet(
      `mail/templates/proforma/${agencyId.value}`
    )) as unknown as GatResponseResult<EmailItem[]>;
    if (Array.isArray(emailTemplatesResult)) {
      emailTemplates.value = emailTemplatesResult;
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
}
function proformaSelected(proformaIDs: number[]) {
  selectedProforma.value = [];
  if (proformaIDs.length > 1) {
    selectedProforma.value.push(proformaIDs[proformaIDs.length - 1]);
  }
  if (proformaIDs.length === 1) {
    selectedProforma.value.push(proformaIDs[0]);
  }
}
function proformaClicked(rowItem: ProformaItem) {
  const params = {
    callId: props.callId,
    proformaId: rowItem.proformaId,
  };
  router.push({ name: 'proformaItem', params });
}
function createEmailClick(item: EmailItem) {
  if (props.callId) {
    const guid = GlobalHelperFunctions.GUID();
    const templateId = item.ID;
    const location = 'portcall';
    const foreignKey = props.callId;
    const appStatus = JSON.stringify(applicationStatus.value);
    const param = { guid, templateId, location, foreignKey, appStatus, isEmail: 1 };
    apiPut('document/generate/', param).then((result) => {
      if (result.resultCategory === 0 && result.resultType === 0) {
        emailTemplateGuid.value = guid;
      }
    });
  }
}
watch(
  () => props.callId,
  (newValue) => {
    if (newValue) {
      getProformas(newValue);
    }
  },
  { immediate: true }
);

return { __sfc: true,props, appStatusStore, applicationStore, portcallStore, helperStore, isLoading, proformaList, selectedProforma, currenciesForProformas, emailTemplateGuid, emailTemplates, agencyId, emailTemplateItems, userCanGenerateDoc, selectedProformaId, applicationStatus, wmButtonGroup, columns, emailGeneratorIsLoading, getProformas, proformaSelected, proformaClicked, createEmailClick, WmButtonGroup, EmailGenerator }
}

})